import { Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { TFunction } from 'i18next'
import { formatBytes, formatDateTime, formatDuration, utcTooltip } from '@/shared/lib/format'
import { relativeFromNow } from './relativeTime'
import type { ActiveSession, SessionHistoryRow } from './sessionApi'

/**
 * Sessiya jadvallarining umumiy ustunlari.
 *
 * NEGA AJRATILDI: faol sessiyalar, tarix va abonent kartochkasi — uch xil
 * jadval, lekin ustunlarning yarmi bir xil. Uch joyda nusxalansa, trafik
 * yo'nalishi (upload/download) birontasida almashib qolishi mumkin, va bu
 * xato faqat hisobotda ko'rinadi.
 *
 * ⚠️ `uploadBytes` = abonent YUBORGAN (`acctinputoctets`),
 *    `downloadBytes` = abonent OLGAN (`acctoutputoctets`) — NAS nuqtai
 *    nazaridan nomlangan, CLAUDE.md §6.
 */

export function trafficColumns<T extends { uploadBytes: number; downloadBytes: number }>(
  t: TFunction,
): ColumnsType<T> {
  return [
    {
      key: 'upload',
      title: t('session.upload'),
      width: 120,
      align: 'right',
      render: (_, row) => (
        <Tooltip title={t('terms.upload')}>{formatBytes(row.uploadBytes)}</Tooltip>
      ),
    },
    {
      key: 'download',
      title: t('session.download'),
      width: 120,
      align: 'right',
      render: (_, row) => (
        <Tooltip title={t('terms.download')}>{formatBytes(row.downloadBytes)}</Tooltip>
      ),
    },
  ]
}

export function networkColumns<
  T extends { framedIp: string | null; callingStationId: string | null; nasIp: string | null },
>(t: TFunction): ColumnsType<T> {
  return [
    {
      key: 'framedIp',
      title: t('session.ip'),
      width: 140,
      render: (_, row) => row.framedIp ?? '—',
    },
    {
      key: 'mac',
      title: t('session.mac'),
      width: 160,
      render: (_, row) => (
        <Typography.Text code={row.callingStationId !== null}>
          {row.callingStationId ?? '—'}
        </Typography.Text>
      ),
    },
    {
      key: 'nasIp',
      title: t('session.nas'),
      width: 130,
      render: (_, row) => row.nasIp ?? '—',
    },
  ]
}

export function activeTimeColumns(t: TFunction): ColumnsType<ActiveSession> {
  return [
    {
      key: 'startTime',
      title: t('session.start'),
      width: 180,
      render: (_, row) =>
        row.startTime ? (
          // Vaqtlar bazada UTC. Sheriklar UTC+4:30 va UTC+5 da ishlaydi,
          // shuning uchun tooltip'da ikkalasi ham ko'rsatiladi
          <Tooltip title={utcTooltip(row.startTime)}>{formatDateTime(row.startTime)}</Tooltip>
        ) : (
          '—'
        ),
    },
    {
      key: 'duration',
      title: t('session.duration'),
      width: 130,
      render: (_, row) => formatDuration(row.sessionTime),
    },
    lastUpdateColumn(t),
  ]
}

/**
 * Oxirgi Interim-Update (`radacct.acctupdatetime`).
 *
 * NEGA QO'SHILDI: bu qiymat API javobida (`lastUpdate`) ALLAQACHON bor
 * edi va `StaleSessionDetector` unga tayanadi — lekin ekranda hech
 * qayerda ko'rinmasdi. Ya'ni operator "sessiya ochiq" degan qatorga
 * qarab turardi, u esa aslida bir necha soatdan beri jim bo'lishi
 * mumkin edi (router qayta yuklangan, Accounting-Stop yetib kelmagan).
 * "Ochiq" va "tirik" — boshqa-boshqa narsa, va farqi faqat shu ustunda
 * ko'rinadi.
 *
 * ⚠️ `lastHeardFrom` mantig'i backendda: `lastUpdate` bo'lmasa sessiya
 * boshlanish vaqtiga tushiladi (yangi ulangan sessiyada Interim hali
 * kelmagan). Bu yerda esa `lastUpdate` YO'Q bo'lsa aynan shu ochiq
 * aytiladi — panel o'zi taxmin qilib "hozirgina" deb yozmaydi.
 */
export function lastUpdateColumn(t: TFunction): ColumnsType<ActiveSession>[number] {
  return {
    key: 'lastUpdate',
    title: t('session.lastUpdate'),
    width: 150,
    render: (_, row) =>
      row.lastUpdate ? (
        // Nisbiy matn — javobning o'zi; aniq vaqt (UTC + tenant + brauzer
        // zonasi) tooltip'da qoladi
        <Tooltip title={utcTooltip(row.lastUpdate)}>{relativeFromNow(row.lastUpdate, t)}</Tooltip>
      ) : (
        <Tooltip title={t('session.lastUpdateNone')}>
          <Typography.Text type="secondary">—</Typography.Text>
        </Tooltip>
      ),
  }
}

export function historyTimeColumns(t: TFunction): ColumnsType<SessionHistoryRow> {
  return [
    {
      key: 'startTime',
      title: t('session.start'),
      width: 180,
      render: (_, row) =>
        row.startTime ? (
          <Tooltip title={utcTooltip(row.startTime)}>{formatDateTime(row.startTime)}</Tooltip>
        ) : (
          '—'
        ),
    },
    {
      key: 'stopTime',
      title: t('session.stop'),
      width: 180,
      render: (_, row) =>
        row.stopTime ? (
          <Tooltip title={utcTooltip(row.stopTime)}>{formatDateTime(row.stopTime)}</Tooltip>
        ) : (
          // Yopilmagan sessiya tarixda ham uchraydi: Accounting-Stop yetib
          // kelmagan bo'lishi mumkin (osilgan sessiya)
          <Typography.Text type="warning">{t('subscriber.ongoing')}</Typography.Text>
        ),
    },
    {
      key: 'duration',
      title: t('session.duration'),
      width: 130,
      render: (_, row) => formatDuration(row.sessionTime),
    },
  ]
}

/**
 * Uzilish sababi — tarjimasi bo'lsa tarjima qilinadi, bo'lmasa xom qiymat.
 *
 * Xom qiymatni ko'rsatish ATAYLAB: RADIUS yangi sabab qaytarsa, uni "—" ga
 * aylantirish diagnostika uchun kerakli ma'lumotni yo'qotardi.
 */
export function terminateCauseColumn(t: TFunction): ColumnsType<SessionHistoryRow>[number] {
  return {
    key: 'terminateCause',
    title: t('session.terminateCause'),
    width: 170,
    render: (_, row) => {
      if (!row.terminateCause) return '—'
      const key = `terminateCause.${row.terminateCause}`
      const translated = t(key)
      return translated === key ? row.terminateCause : translated
    },
  }
}
